import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Copy, Check, Linkedin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ChallengeCertificatePreviewProps {
  certificate: {
    id: string;
    certificate_number: string;
    issued_at: string;
    total_challenges: number;
    completed_challenges: number;
    accuracy_percentage: number;
    category: string;
  };
  userName: string;
  certificateTitle: string;
  onClose: () => void;
}

export function ChallengeCertificatePreview({
  certificate,
  userName,
  certificateTitle,
  onClose,
}: ChallengeCertificatePreviewProps) {
  const [copied, setCopied] = useState(false);
  const isPreview = certificate.certificate_number?.startsWith('PREVIEW-');

  const verificationUrl = `${window.location.origin}/verificar/${certificate.certificate_number}`;
  const shareUrl = `https://jdwcgbwcwkrrvaqtokju.supabase.co/functions/v1/certificate-share-page?code=${encodeURIComponent(certificate.certificate_number)}`;
  const issuedDate = new Date(certificate.issued_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const logoUrl = `${window.location.origin}/lovable-uploads/logo-horizontal.png`;

  const registerShare = async (platform: "linkedin" | "whatsapp" | "copy") => {
    try {
      const { data, error } = await supabase.functions.invoke("register-certificate-share", {
        body: { certificate_id: certificate.id, platform },
      });
      if (error) return;
      if (data?.pointsEarned > 0) {
        toast.success(`🎁 +${data.pointsEarned} pts pelo compartilhamento!`);
      }
    } catch (e) {
      // silent fail — share already happened on the client
    }
  };

  const buildLinkedInShareText = () =>
    `🎓 Acabei de conquistar o certificado de ${certificateTitle} no @datahireclub!\n\n` +
    `O Data Hire Club é a plataforma que conecta talentos em dados com as melhores empresas do Brasil — com trilhas práticas, desafios técnicos e uma comunidade ativa.\n\n` +
    `Quer evoluir na carreira de dados também? Conheça: ${shareUrl}\n\n` +
    `#DataHireClub #CarreiraEmDados #Certificação`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(verificationUrl);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
    registerShare("copy");
  };

  const handleShareLinkedIn = () => {
    const text = buildLinkedInShareText();
    const url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "width=600,height=600");
    registerShare("linkedin");
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Certificado - ${certificateTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: landscape; margin: 0; }
            html, body { 
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body { 
              font-family: 'Georgia', serif;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              background: #f5f5f5;
            }
            .certificate {
              width: 297mm;
              height: 210mm;
              background: linear-gradient(135deg, #0c1220 0%, #1a1a3e 50%, #0d2847 100%) !important;
              color: white;
              padding: 40px;
              position: relative;
              overflow: hidden;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            .border-decoration {
              position: absolute;
              top: 20px;
              left: 20px;
              right: 20px;
              bottom: 20px;
              border: 2px solid rgba(34,197,94,0.3);
              pointer-events: none;
            }
            .corner {
              position: absolute;
              width: 60px;
              height: 60px;
              border: 3px solid #22c55e;
            }
            .corner-tl { top: 30px; left: 30px; border-right: none; border-bottom: none; }
            .corner-tr { top: 30px; right: 30px; border-left: none; border-bottom: none; }
            .corner-bl { bottom: 30px; left: 30px; border-right: none; border-top: none; }
            .corner-br { bottom: 30px; right: 30px; border-left: none; border-top: none; }
            .content {
              position: relative;
              z-index: 1;
              text-align: center;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .header { margin-top: 20px; }
            .logo-img { height: 50px; margin-bottom: 10px; }
            .subtitle { font-size: 14px; color: rgba(255,255,255,0.7); margin-top: 5px; }
            .title-section { margin: 25px 0; }
            .certificate-label { 
              font-size: 14px; 
              letter-spacing: 8px; 
              color: rgba(255,255,255,0.5);
              text-transform: uppercase;
            }
            .certificate-title { 
              font-size: 42px; 
              font-weight: bold;
              color: #22c55e;
              margin: 12px 0;
              text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
            }
            .recipient-section { margin: 15px 0; }
            .conferido { font-size: 14px; color: rgba(255,255,255,0.7); }
            .name { 
              font-size: 36px; 
              font-style: italic;
              color: white;
              margin: 10px 0;
              border-bottom: 2px solid rgba(34,197,94,0.5);
              padding-bottom: 10px;
              display: inline-block;
            }
            .course { font-size: 18px; margin: 12px 0; }
            .course-name { color: #22c55e; font-weight: bold; }
            .details { 
              font-size: 14px; 
              color: rgba(255,255,255,0.8);
              margin: 8px 0;
            }
            .accuracy-badge {
              display: inline-block;
              background: rgba(34,197,94,0.15);
              border: 1px solid rgba(34,197,94,0.4);
              border-radius: 6px;
              padding: 4px 16px;
              font-size: 14px;
              color: #22c55e;
              font-weight: bold;
              margin-top: 8px;
            }
            .footer {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              margin-bottom: 20px;
              padding: 0 40px;
            }
            .date-section, .code-section { text-align: left; }
            .code-section { text-align: right; }
            .label { font-size: 12px; color: rgba(255,255,255,0.6); }
            .value { font-size: 14px; color: white; font-family: monospace; }
            .signature-section { text-align: center; }
            .signature-line { 
              width: 200px; 
              border-top: 1px solid rgba(255,255,255,0.5);
              margin: 0 auto;
              padding-top: 5px;
            }
            .signature-name { font-size: 14px; }
            .signature-title { font-size: 12px; color: rgba(255,255,255,0.6); }
            @media print {
              body { background: none !important; }
              .certificate { 
                box-shadow: none;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="border-decoration"></div>
            <div class="corner corner-tl"></div>
            <div class="corner corner-tr"></div>
            <div class="corner corner-bl"></div>
            <div class="corner corner-br"></div>
            <div class="content">
              <div class="header">
                <img src="${logoUrl}" alt="Data Hire Club" class="logo-img" />
                <div class="subtitle">Plataforma de Carreiras em Dados</div>
              </div>
              <div class="title-section">
                <div class="certificate-label">Certificado de Proficiência</div>
                <div class="certificate-title">DESAFIOS TÉCNICOS</div>
                
              </div>
              <div class="recipient-section">
                <div class="conferido">Conferido a</div>
                <div class="name">${userName}</div>
                <div class="course">
                  por demonstrar proficiência em <span class="course-name">${certificateTitle}</span>
                </div>
                <div class="details">
                  ${certificate.completed_challenges} de ${certificate.total_challenges} desafios concluídos com sucesso
                </div>
                <div class="accuracy-badge">Aproveitamento: ${certificate.accuracy_percentage}%</div>
              </div>
              <div class="footer">
                <div class="date-section">
                  <div class="label">Data de Emissão</div>
                  <div class="value">${issuedDate}</div>
                </div>
                <div class="signature-section">
                  <div class="signature-line">
                    <div class="signature-name">Data Hire Club</div>
                    <div class="signature-title">Coordenação Técnica</div>
                  </div>
                </div>
                <div class="code-section">
                  <div class="label">Código de Verificação</div>
                  <div class="value">${certificate.certificate_number}</div>
                </div>
              </div>
            </div>
          </div>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Certificado de Desafios Técnicos</DialogTitle>
        </DialogHeader>

        {/* Certificate Preview - green theme for challenges */}
        <div className="bg-gradient-to-br from-[#0c1220] via-[#1a1a3e] to-[#0d2847] text-white rounded-lg p-8 relative overflow-hidden">
          <div className="absolute inset-4 border border-emerald-500/30 rounded pointer-events-none" />

          <div className="text-center space-y-6 relative z-10">
            <div>
              <img
                src="/lovable-uploads/logo-horizontal.png"
                alt="Data Hire Club"
                className="h-12 mx-auto mb-2"
              />
              <p className="text-sm text-white/60">Plataforma de Carreiras em Dados</p>
            </div>

            <div>
              <p className="text-xs tracking-[0.3em] text-white/50 uppercase">Certificado de Proficiência</p>
              <h1 className="text-3xl font-bold text-emerald-400 mt-2">DESAFIOS TÉCNICOS</h1>
              
            </div>

            <div className="space-y-2">
              <p className="text-sm text-white/70">Conferido a</p>
              <p className="text-2xl italic border-b border-emerald-500/30 pb-2 inline-block px-8">
                {userName}
              </p>
            </div>

            <div>
              <p className="text-base">
                por demonstrar proficiência em <span className="text-emerald-400 font-semibold">{certificateTitle}</span>
              </p>
              <p className="text-sm text-white/80 mt-2">
                {certificate.completed_challenges} de {certificate.total_challenges} desafios concluídos com sucesso
              </p>
              <span className="inline-block mt-2 bg-emerald-500/15 border border-emerald-500/40 rounded-md px-4 py-1 text-sm text-emerald-400 font-bold">
                Aproveitamento: {certificate.accuracy_percentage}%
              </span>
            </div>

            <div className="flex justify-between items-end pt-4">
              <div className="text-left">
                <p className="text-xs text-white/50">Data de Emissão</p>
                <p className="text-sm font-mono">{issuedDate}</p>
              </div>
              <div className="text-center">
                <div className="border-t border-white/30 pt-2 px-8">
                  <p className="text-sm">Data Hire Club</p>
                  <p className="text-xs text-white/60">Coordenação Técnica</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/50">Código de Verificação</p>
                <p className="text-sm font-mono">{certificate.certificate_number}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 mt-4">
          {isPreview && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 text-center">
              Modo pré-visualização — este certificado não está registrado e não pode ser verificado ou compartilhado.
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={handlePrint} className="flex-1">
              <Download className="h-4 w-4 mr-2" />
              Baixar PDF
            </Button>
            {!isPreview && (
              <>
                <Button variant="outline" onClick={handleCopyLink} className="flex-1">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  Copiar Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(verificationUrl, '_blank')}
                  className="flex-1"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Verificar Online
                </Button>
              </>
            )}
          </div>

          {!isPreview && (
            <div className="border-t pt-3">
              <p className="text-xs text-muted-foreground mb-2 text-center">
                Compartilhe sua conquista e ganhe <span className="font-semibold text-primary">+15 pts</span> no ranking
              </p>
              <Button
                onClick={handleShareLinkedIn}
                className="w-full bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white"
              >
                <Linkedin className="h-4 w-4 mr-2" />
                Compartilhar no LinkedIn
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
